import { useMemo, useState } from "react";
import { format, startOfDay, endOfDay, isSameDay } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { presets } from "../lib/dates";

// Bottom-edge dot on each day cell, tinted by that day's group volume — lets a manager
// spot busy days before committing to a range, instead of picking blind.
const HEAT_CLASS = {
  high: "relative after:content-[''] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-blue-600",
  med: "relative after:content-[''] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-blue-400",
  low: "relative after:content-[''] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-blue-200",
};

// `trigger(label)` swaps the default pill for the caller's own button.
// `compact` is for narrow side panels: the popup opens from the trigger's left
// edge and the range calendar shows one month, so nothing spills out of the panel.
export function DateRangePicker({ range, onChange, activity, disabled, trigger, compact = false }) {
  const [open, setOpen] = useState(false);
  const [pickMode, setPickMode] = useState("single"); // "single" | "range"
  // A range is two clicks: the first starts a new one, the second ends it. Held
  // here, not read from `range`, so a click never edits the range already chosen.
  const [draft, setDraft] = useState(null);

  const openChanged = (next) => {
    setOpen(next);
    setDraft(null);
  };
  const pickDay = (day) => {
    if (!draft?.from || draft.to) return setDraft({ from: day, to: undefined });
    const [from, to] = day < draft.from ? [day, draft.from] : [draft.from, day];
    onChange({ from: startOfDay(from), to: endOfDay(to) });
    openChanged(false);
  };

  const modifiers = useMemo(() => {
    const maxCount = Math.max(1, ...Array.from(activity.values()));
    const high = [], med = [], low = [];
    activity.forEach((count, key) => {
      const date = new Date(`${key}T00:00:00`);
      const ratio = count / maxCount;
      (ratio > 0.66 ? high : ratio > 0.33 ? med : low).push(date);
    });
    return { high, med, low };
  }, [activity]);

  const isSingleDay = range?.from && range?.to && isSameDay(range.from, range.to);
  const label =
    range?.from && range?.to
      ? isSingleDay
        ? format(range.from, "MMM d, yyyy")
        : `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`
      : "Pick a date";

  return (
    <Popover open={open} onOpenChange={openChanged}>
      <PopoverTrigger asChild>
        {trigger ? (
          trigger(label)
        ) : (
          <button
            type="button"
            disabled={disabled}
            data-testid="date-range-trigger"
            title={disabled ? "Waiting on live group timestamps" : undefined}
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
          >
            <CalendarIcon size={14} />
            {label}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="max-h-[var(--radix-popover-content-available-height)] w-auto overflow-auto p-0"
        align={compact ? "start" : "end"}
        collisionPadding={12}
      >
        <div className="flex">
          <div className="flex w-36 flex-col gap-1 border-r border-slate-100 p-2">
            {presets().map((p) => (
              <button
                key={p.label}
                type="button"
                data-testid={`date-preset-${p.label.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => {
                  onChange(p.range);
                  openChanged(false);
                }}
                className="rounded-md px-2.5 py-1.5 text-left text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="p-2">
            <div className="flex rounded-full border border-slate-200 bg-white p-0.5 mb-2 w-fit" data-testid="date-pick-mode">
              {["single", "range"].map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`date-pick-mode-${m}`}
                  onClick={() => setPickMode(m)}
                  className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${
                    pickMode === m ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {m === "single" ? "One day" : "Range"}
                </button>
              ))}
            </div>
            {pickMode === "single" ? (
              <Calendar
                mode="single"
                selected={isSingleDay ? range.from : undefined}
                onSelect={(d) => {
                  if (!d) return;
                  onChange({ from: startOfDay(d), to: endOfDay(d) });
                  openChanged(false);
                }}
                numberOfMonths={1}
                modifiers={modifiers}
                modifiersClassNames={HEAT_CLASS}
              />
            ) : (
              <Calendar
                mode="range"
                selected={draft || { from: range?.from, to: range?.to }}
                onDayClick={pickDay}
                numberOfMonths={compact ? 1 : 2}
                modifiers={modifiers}
                modifiersClassNames={HEAT_CLASS}
              />
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
